import { effect, inject, Injectable } from "@angular/core";
import { safeParseFloat } from "../../../../../../shared/src/lib/utils/common";
import { LiveUpdateService } from "../../../../../../shared/src/lib/services/live-update.service";
import { ApiService } from "../../../../../../shared/src";
import { Router } from "@angular/router";
import { BehaviorSubject, combineLatest, Observable } from "rxjs";
import { BetPlacementService } from "../../../../../../shared/src/lib/services/bet-placement.service";
import { BalanceService } from "../../../../../../shared/src/lib/services/balance.service";
import { ToasterService } from "../../../../../../shared/src/lib/services/toaster.service";

@Injectable({
  providedIn: "root",
})
export class PlacementOpsService {
  msgOverlay: BehaviorSubject<boolean> = new BehaviorSubject(false);
  liveUpdateService = inject(LiveUpdateService);
  apiService = inject(ApiService);
  placementService = inject(BetPlacementService);
  toaster = inject(ToasterService);
  router = inject(Router);
  success$: BehaviorSubject<number> = new BehaviorSubject(0);
  error$: BehaviorSubject<number> = new BehaviorSubject(0);
  timeout: any = null;
  shouldClearParams = false;
  currentParams: any = null;
  submittedParams: any = null;
  balanceService = inject(BalanceService);
  isPlacingBet: boolean = false;

  constructor() {
    effect(() => {
      const data = this.liveUpdateService._getData(["wallet", "bet_placed"]);
      if (data) {
        this.handlePlacementUpdate(data);
      }
    });
    this.validateMarketStatus();
  }

  validateMarketStatus() {
    const param$ = this.placementService.getParams();
    const marketUpdate$ = this.liveUpdateService.getData();

    combineLatest([param$, marketUpdate$]).subscribe(([params, update]) => {
      if (!params) return;

      const { topic, PROVIDER, SELECTION_ID, MARKET_ID } = params?.data || {};
      const isBf = PROVIDER === "BF";

      if (isBf) {
        if (update && update?.id && update?.id === MARKET_ID) {
          if (Number.isFinite(update?.st) && update?.st !== 2) {
            this.placementService.disablePlacement$.next(true);
          }
        }
      } else {
        if (update && update?.topic && update?.topic === topic) {
          const runnerUpdate = update?.runners?.find((r: any) => Boolean(r?.selectionId === SELECTION_ID));
          // console.log(runnerUpdate);
          if (Boolean(runnerUpdate) && runnerUpdate?.status !== "ACTIVE") {
            this.placementService.disablePlacement$.next(true);
          }
        }
      }
    });
  }

  priceValue(p: any, fOdds?: any) {
    if (!this.currentParams || !p) return 0;
    return fOdds;
  }

  setMsgOverlay(value: boolean) {
    this.msgOverlay.next(value);
  }

  getMsgOverlay(): Observable<boolean> {
    return this.msgOverlay as Observable<boolean>;
  }

  resetStatus() {
    this.success$.next(0);
    this.error$.next(0);
  }

  setTransactionTimeout(betId: number) {
    this.timeout = setTimeout(() => {
      this.placementService.checkBetStatus(betId);
      this.balanceService.refreshExposure();
      this.clearParams();
      this.handleError();
    }, 20000);
  }

  resetTimeout() {
    if (this.timeout) clearTimeout(this.timeout);
    this.timeout = null;
    this.isPlacingBet = false;
  }

  handlePlacementUpdate(update: any) {
    const authToken = this.apiService.getUserToken();
    const userId = authToken?.user?.ID;
    const data = update?.data;
    const type = update?.type;
    const walletId = this.apiService.getUserWalletId();
    const isWalletUpdate = data && type === "wallet" && data?.id === walletId;
    const isPlacementUpdate = data && type === "bet_placed" && data?.USER_ID === userId;

    // console.log(this.transactionTimeout);
    if (this.isPlacingBet) {
      if (isPlacementUpdate) {
        console.log("placement update", update);
        if (data?.STATUS === "Success") {
          this.handleSuccess("placement", data);
        } else {
          this.handleError(data?.BF_MESSAGE);
        }
      } else if (isWalletUpdate) {
        console.log("wallet update", update);
        if (this.success$.value === 0 && this.error$.value === 0) {
          this.handleSuccess("wallet");
        }
      }
    }
  }

  handleSuccess(reason: string, data?: any) {
    const refId = data?.ID;
    const sizeRemaining = safeParseFloat(data?.SIZE_REMAINING);
    const sizeMatched = safeParseFloat(data?.SIZE_MATCHED);
    const matched = sizeRemaining === 0 && sizeMatched > 0;
    const unmatched = sizeRemaining > 0 && sizeMatched === 0;
    const partiallyMatched = sizeRemaining > 0 && sizeMatched > 0;
    const title = "Placement Successful";
    this.success$.next(this.success$.value + 1);
    if (reason === "placement") {
      if (matched) {
        this.toaster.success(title, `Bet ID: ${refId} was successfully matched.`);
      } else if (unmatched) {
        this.toaster.success("Unmatched Bet Placed", `Bet ID: ${refId} unmatched bet was placed successfully.`);
      } else if (partiallyMatched) {
        this.toaster.warning("Partially Matched Bet Placed", `Bet ID: ${refId} partially matched bet was placed successfully.`);
      } else {
        this.toaster.success(title, `Bet ID: ${refId} was successfully placed.`);
      }
    } else {
      this.toaster.success(title, `Bet was successfully placed.`);
    }

    this.setMsgOverlay(false);
    this.placementService.disablePlacement$.next(true);

    this.resetTimeout();
    if (this.shouldClearParams) {
      this.clearParams();
    } else {
      this.shouldClearParams = true;
    }
  }

  handleError(message?: string) {
    this.error$.next(this.error$.value + 1);
    console.log("Submitted params on error", this.submittedParams);
    if (message) {
      this.toaster.error("Error", message || "Unable to place your bets.");
    }
    this.resetTimeout();
    this.setMsgOverlay(false);
    localStorage.removeItem("placing_bet_flag");
  }

  clearParams() {
    this.placementService.setParams(null);
  }
}
